import { getAmphursByProvinceList, getDistrictByAmphurID } from '../../utils/requests';
import { LocationItem } from '../../types/search';
import { highlightText } from './utils';

let debounceTimer: NodeJS.Timeout | null = null;

let provinceLocationCache: { [key: string]: LocationItem[] } = {};

export const loadProvinceSubLocations = async (
  location: LocationItem,
  searchQuery: string,
  setLoadingLocationData: (loading: boolean) => void,
  setSelectedMainOption: (option: LocationItem) => void,
  locale: string = 'th'
) => {
  if (!location) {
    return;
  }

  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(async () => {
    try {
      const cacheKey = `${location.typeId}_${location.value}_${location.isAmphur ? 'amphur' : 'province'}`;
      const hasCache = provinceLocationCache[cacheKey]?.length > 0;
      
      if (!hasCache) {
        setLoadingLocationData(true);
      }

      let subLocations: LocationItem[] = [];
      
      if (hasCache) {
        subLocations = provinceLocationCache[cacheKey];
      } else {
        if (location.isAmphur) {
          subLocations = await loadDistrictsByAmphurId(location.value.toString(), location, locale);
        } else if (location.typeId === 0 && !location.isAmphur && !location.isDistrict) {
          subLocations = await loadAmphursByProvinceId(location.value.toString(), location, locale);
        }
        
        if (subLocations.length > 0) {
          provinceLocationCache[cacheKey] = [...subLocations];
        }
      }
      
      let filteredSubLocations = filterLocationsBySearch(subLocations, searchQuery);
      
      const updatedLocation = {
        ...location,
        children: filteredSubLocations
      };
      
      setSelectedMainOption(updatedLocation);
    } catch (error) {
      setSelectedMainOption({
        ...location,
        children: []
      });
    } finally {
      setLoadingLocationData(false);
    }
  }, 100);
};

const loadAmphursByProvinceId = async (provinceId: string, parentLocation: LocationItem, locale: string): Promise<LocationItem[]> => {
  try {
    const response = await getAmphursByProvinceList([provinceId]);
    
    if (response && response.data && Array.isArray(response.data)) {
      const amphurs = response.data
        .filter(amphur => {
          const amphurName = amphur.AMPHUR_NAME_TH;
          const hasValidStatus = amphur.AMPHUR_STATUS === undefined || amphur.AMPHUR_STATUS === 1;
          const hasValidName = amphurName && 
                              amphurName.trim() !== '' && 
                              amphurName !== 'ไม่มีชื่อ' && 
                              amphurName !== 'ทั้งหมด';
          
          return hasValidStatus && hasValidName;
        })
        .map(amphur => ({
          label: locale === 'th' ? (amphur.AMPHUR_NAME_TH || amphur.AMPHUR_NAME_EN || amphur.AMPHUR_NAME_CN) :
                 locale === 'en' ? (amphur.AMPHUR_NAME_EN || amphur.AMPHUR_NAME_TH || amphur.AMPHUR_NAME_CN) :
                 (amphur.AMPHUR_NAME_CN || amphur.AMPHUR_NAME_TH || amphur.AMPHUR_NAME_EN),
          value: amphur.AMPHUR_ID,
          typeId: 0,
          isAmphur: true,
          lat: amphur.AMPHUR_LAT || 0,
          lng: amphur.AMPHUR_LON || 0,
          parentId: parentLocation.value.toString(),
          parentName: parentLocation.label,
          originalLabel: locale === 'th' ? (amphur.AMPHUR_NAME_TH || amphur.AMPHUR_NAME_EN || amphur.AMPHUR_NAME_CN) :
                        locale === 'en' ? (amphur.AMPHUR_NAME_EN || amphur.AMPHUR_NAME_TH || amphur.AMPHUR_NAME_CN) :
                        (amphur.AMPHUR_NAME_CN || amphur.AMPHUR_NAME_TH || amphur.AMPHUR_NAME_EN),
          rendered: false,
          provinceId: parentLocation.value
        }));
      
      return amphurs;
    }
    
    return [];
  } catch (error) {
    return [];
  }
};

const loadDistrictsByAmphurId = async (amphurId: string, parentLocation: LocationItem, locale: string): Promise<LocationItem[]> => {
  try {
    const response = await getDistrictByAmphurID(amphurId);
    
    if (response && response.data && Array.isArray(response.data)) {
      const districts = response.data
        .filter(district => {
          const districtName = district.DISTRICT_NAME_TH;
          return district.DISTRICT_STATUS === 1 && 
                 districtName && 
                 districtName.trim() !== '' && 
                 districtName !== 'ไม่มีชื่อ' && 
                 districtName !== 'ทั้งหมด';
        })
        .map(district => ({
          label: locale === 'th' ? (district.DISTRICT_NAME_TH || district.DISTRICT_NAME_EN || district.DISTRICT_NAME_CN) :
                 locale === 'en' ? (district.DISTRICT_NAME_EN || district.DISTRICT_NAME_TH || district.DISTRICT_NAME_CN) :
                 (district.DISTRICT_NAME_CN || district.DISTRICT_NAME_TH || district.DISTRICT_NAME_EN),
          value: district.DISTRICT_ID,
          typeId: 0,
          isDistrict: true,
          lat: district.DISTRICT_LAT || 0,
          lng: district.DISTRICT_LON || 0,
          parentId: parentLocation.value.toString(),
          parentName: `${parentLocation.parentName} > ${parentLocation.label}`,
          originalLabel: locale === 'th' ? (district.DISTRICT_NAME_TH || district.DISTRICT_NAME_EN || district.DISTRICT_NAME_CN) :
                        locale === 'en' ? (district.DISTRICT_NAME_EN || district.DISTRICT_NAME_TH || district.DISTRICT_NAME_CN) :
                        (district.DISTRICT_NAME_CN || district.DISTRICT_NAME_TH || district.DISTRICT_NAME_EN),
          rendered: false,
          amphurId: parentLocation.value,
          provinceId: parentLocation.provinceId
        }));
      
      return districts;
    }
    
    return [];
  } catch (error) {
    return [];
  }
};

const filterLocationsBySearch = (locations: LocationItem[], searchQuery: string): LocationItem[] => {
  if (!searchQuery || searchQuery.trim() === '') {
    return locations;
  }
  
  const filteredLocations = locations.filter(location => 
    location.label.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  if (filteredLocations.length === 0) {
    return locations;
  }
  
  return filteredLocations.map(location => {
    const highlighted = highlightText(location.label, searchQuery);
    return {
      ...location,
      displayLabel: highlighted.displayLabel,
      originalLabel: highlighted.originalLabel,
      rendered: highlighted.rendered
    };
  });
};

export const isProvinceLocationHandling = (location: LocationItem): boolean => {
  return Boolean((location.typeId === 0 || location.typeId === null) || location.isAmphur || location.isDistrict);
};

export const clearProvinceLocationCache = (): void => {
  provinceLocationCache = {};
}; 